/**
 * Pure derivations over the golden-process payload — shared by the
 * timeline, the runs table, and the step-outcome grid.
 */

import type {
  GoldenStep,
  ProcessRollout,
  RolloutEntry,
  TaskProcessResponse,
  TrajectoryMessage,
} from "@/api/generated/model";

export interface MappedFailure {
  rollout: ProcessRollout;
  tag: string;
  confidence: string;
  rootCause: string;
}

/** Failures grouped by the step their diagnosis names. */
export function failuresByStep(
  process: TaskProcessResponse,
): Map<string, MappedFailure[]> {
  const map = new Map<string, MappedFailure[]>();
  for (const rollout of process.rollouts) {
    if (rollout.failure === null) continue;
    const entry: MappedFailure = {
      rollout,
      tag: rollout.failure.primary_tag,
      confidence: rollout.failure.confidence,
      rootCause: rollout.failure.root_cause,
    };
    const existing = map.get(rollout.failure.primary_step_id);
    if (existing === undefined) {
      map.set(rollout.failure.primary_step_id, [entry]);
    } else {
      existing.push(entry);
    }
  }
  return map;
}

function median(values: number[]): number | null {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const hi = sorted[mid];
  if (hi === undefined) return null;
  const lo = sorted[mid - 1];
  return sorted.length % 2 === 1 || lo === undefined ? hi : (lo + hi) / 2;
}

export interface StepSpan {
  start: number | null;
  end: number | null;
}

/** Median turn window of each step across every rollout that ran it. */
export function stepSpans(process: TaskProcessResponse): Map<string, StepSpan> {
  const map = new Map<string, StepSpan>();
  for (const step of process.steps) {
    const runs = process.rollouts
      .flatMap((rollout) => rollout.step_runs)
      .filter((run) => run.step_id === step.id);
    const start = median(runs.map((run) => run.turn_start));
    const end = median(runs.map((run) => run.turn_end));
    map.set(step.id, {
      start,
      end: start !== null && end !== null ? Math.max(start, end) : end,
    });
  }
  return map;
}


/**
 * Join a process rollout to its rollout-strip entry. Lane exporters share
 * literal ids (r0, r1, ...); GDPVal-style exporters publish opaque dirs
 * (traj_<hex>), where the process side's r<n> is the within-model ordinal
 * in the SAME sorted order the strip lists, so the nth entry is the match.
 */
export function processEntryFor(
  rollout: { model: string; rollout_id: string },
  entries: RolloutEntry[]
): RolloutEntry | undefined {
  const direct = entries.find(
    (e) => e.model === rollout.model && e.id === rollout.rollout_id
  );
  if (direct !== undefined) return direct;
  const ordinal = /^r(\d+)$/.exec(rollout.rollout_id);
  if (ordinal === null) return undefined;
  return entries.filter((e) => e.model === rollout.model)[Number(ordinal[1])];
}

/** The reverse join: strip entry -> its process rollout. */
export function processRolloutForEntry(
  entry: { model: string; id: string },
  entries: RolloutEntry[],
  rollouts: ProcessRollout[]
): ProcessRollout | undefined {
  const direct = rollouts.find(
    (r) => r.model === entry.model && r.rollout_id === entry.id
  );
  if (direct !== undefined) return direct;
  const ordinal = entries
    .filter((e) => e.model === entry.model)
    .findIndex((e) => e.id === entry.id);
  if (ordinal === -1) return undefined;
  return rollouts.find(
    (r) => r.model === entry.model && r.rollout_id === `r${ordinal.toString()}`
  );
}

export function humanizeTag(tag: string): string {
  return tag.split("_").join(" ");
}

/** Outcome fills shared by the outcome grid and the transcript step rail. */
const OUTCOME_FILLS: Record<string, string> = {
  ok: "hsl(var(--success) / 0.55)",
  partial: "hsl(var(--warning) / 0.5)",
  wrong: "hsl(var(--destructive) / 0.5)",
  dead_end: "hsl(var(--destructive) / 0.75)",
  skipped: "hsl(var(--muted-foreground) / 0.2)",
};

export const KNOWN_OUTCOMES = Object.keys(OUTCOME_FILLS);

export function outcomeFill(outcome: string): string {
  const fill = OUTCOME_FILLS[outcome] as string | undefined;
  return fill ?? "hsl(var(--muted-foreground) / 0.35)";
}

/** What each analyzer outcome label means, for legends and tooltips. */
const OUTCOME_HELP: Record<string, string> = {
  ok: "The run completed this step correctly.",
  partial: "The run did this step incompletely.",
  wrong: "The run produced an incorrect result at this step.",
  dead_end: "The run's attempt at this step went nowhere and was abandoned.",
  skipped: "The run never attempted this step.",
};

export function outcomeHelp(outcome: string): string {
  const help = OUTCOME_HELP[outcome] as string | undefined;
  return help ?? "The analyzer used a label this app does not recognize.";
}

/**
 * The pass bar for a whole run: 90% or better. Deliberately stricter
 * than the difficulty threshold (0.67), so a green path means the run
 * genuinely succeeded rather than merely clearing the Easy bar.
 */
export const PASS_BAR = 0.9;

/**
 * The first non-empty what_happened among a tree node's runs at a
 * step: what this branch actually did there, from the analysis labels.
 */
export function whatHappenedAt(
  node: TrajectoryTreeNode,
  stepId: string,
): string | null {
  for (const rollout of node.rollouts) {
    const run = rollout.step_runs.find(
      (r) => r.step_id === stepId && r.what_happened !== "",
    );
    if (run !== undefined) return run.what_happened;
  }
  return null;
}

export interface TurnEventRange {
  turn: number;
  start: number;
  end: number;
}

/**
 * Which transcript-event indexes each assistant turn spans, mirroring
 * trajectoryToEvents' emission exactly (assistant text + one event per
 * tool call; tool results attach to their call and emit nothing; an
 * orphan tool output emits a raw row). A turn runs from its assistant
 * message's first event to the event before the next turn starts, so
 * rail segments can land exactly on row boundaries.
 */
export function turnEventRanges(
  messages: TrajectoryMessage[],
): TurnEventRange[] {
  let eventIndex = 0;
  let pendingCalls = 0;
  const starts: number[] = [];
  for (const message of messages) {
    switch (message.role) {
      case "system":
      case "user":
        eventIndex += 1;
        break;
      case "assistant": {
        starts.push(eventIndex);
        if (message.content.trim() !== "") eventIndex += 1;
        const calls = message.tool_calls?.length ?? 0;
        eventIndex += calls;
        pendingCalls += calls;
        break;
      }
      case "tool":
        if (pendingCalls > 0) {
          pendingCalls -= 1;
        } else {
          eventIndex += 1;
        }
        break;
      default:
        eventIndex += 1;
    }
  }
  return starts.map((start, i) => {
    const next = starts[i + 1];
    return {
      turn: i + 1,
      start,
      end: (next ?? eventIndex) - 1,
    };
  });
}

/**
 * The trajectory tree: one node per golden-process step, so depth in the
 * tree IS the step index and every path runs down to the last step.
 * Rollouts merge in a node while they share the same outcome at that
 * step; a differing outcome forks them into siblings. `branchStep` is
 * the NEXT step this node's children sit on (null past the last step).
 */
export interface TrajectoryTreeNode {
  /** This node's outcome at the parent's branch step; null at the root. */
  outcome: string | null;
  branchStep: GoldenStep | null;
  children: TrajectoryTreeNode[];
  rollouts: ProcessRollout[];
}

function outcomeAt(rollout: ProcessRollout, stepId: string): string {
  return (
    rollout.step_runs.find((run) => run.step_id === stepId)?.outcome ??
    "skipped"
  );
}

function buildNode(
  process: TaskProcessResponse,
  group: ProcessRollout[],
  stepIndex: number,
  outcome: string | null,
): TrajectoryTreeNode {
  const branchStep = process.steps[stepIndex];
  if (branchStep === undefined) {
    return { outcome, branchStep: null, children: [], rollouts: group };
  }
  const byOutcome = new Map<string, ProcessRollout[]>();
  for (const rollout of group) {
    const key = outcomeAt(rollout, branchStep.id);
    const existing = byOutcome.get(key);
    if (existing === undefined) {
      byOutcome.set(key, [rollout]);
    } else {
      existing.push(rollout);
    }
  }
  const children = [...byOutcome.entries()]
    .sort((a, b) => b[1].length - a[1].length)
    .map(([childOutcome, childGroup]) =>
      buildNode(process, childGroup, stepIndex + 1, childOutcome),
    );
  return { outcome, branchStep, children, rollouts: group };
}

export function buildTrajectoryTree(
  process: TaskProcessResponse,
): TrajectoryTreeNode {
  return buildNode(process, process.rollouts, 0, null);
}

/** Index of the first step whose outcomes differ across rollouts. */
export function branchPointIndex(process: TaskProcessResponse): number | null {
  for (const [index, step] of process.steps.entries()) {
    const outcomes = new Set(
      process.rollouts
        .map((r) => r.step_runs.find((run) => run.step_id === step.id)?.outcome)
        .filter((outcome): outcome is string => outcome !== undefined),
    );
    if (outcomes.size > 1) return index;
  }
  return null;
}
